import React, { useState, useEffect, useMemo } from 'react';
import { Spinner } from 'react-bootstrap';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { LineChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { toast } from 'react-toastify';

echarts.use([
  LineChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  CanvasRenderer,
]);

const COLORS = [
  '#2c7be5',
  '#00d27a',
  '#e63757',
  '#f5803e',
  '#27bcfd',
  '#6b5eae',
  '#e6a817',
  '#02a8b5',
];

const formatLabel = (isoStr, interval) => {
  const d = new Date(isoStr);
  if (interval === 'day') {
    return `${d.getMonth() + 1}/${d.getDate()}`;
  }
  return `${String(d.getHours()).padStart(2, '0')}:${String(
    d.getMinutes(),
  ).padStart(2, '0')}`;
};

const ProductivityChart = ({ fetchFn, start, end, interval }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setData(null);
      try {
        const result = await fetchFn({ start, end, interval });
        if (!cancelled) setData(result);
      } catch (error) {
        if (!cancelled && error.response?.status !== 429) {
          toast.error('Failed to load chart data.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setFetched(true);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [fetchFn, start, end, interval]);

  const option = useMemo(() => {
    if (!data?.labels?.length) return null;

    return {
      tooltip: {
        trigger: 'axis',
      },
      legend: {
        bottom: 0,
        type: 'scroll',
        data: data.datasets.map(ds => ds.username),
      },
      grid: { left: 50, right: 20, top: 16, bottom: 40 },
      xAxis: {
        type: 'category',
        data: data.labels.map(l => formatLabel(l, interval)),
        boundaryGap: false,
        axisLabel: { fontSize: 11 },
      },
      yAxis: {
        type: 'value',
        minInterval: 1,
      },
      series: data.datasets.map((ds, i) => ({
        name: ds.username,
        type: 'line',
        data: ds.data,
        smooth: false,
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: { width: 2 },
        itemStyle: { color: COLORS[i % COLORS.length] },
      })),
    };
  }, [data, interval]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-6">
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  if (fetched && (!data?.datasets?.length || !data?.labels?.length)) {
    return (
      <div className="text-center text-600 py-6">
        No data available for the selected period.
      </div>
    );
  }

  if (!option) return null;

  return (
    <ReactEChartsCore
      echarts={echarts}
      option={option}
      style={{ height: 400 }}
      notMerge
    />
  );
};

export default ProductivityChart;
